import { useCallback, useState } from "react";
import { toast } from "sonner";
import { errorMessage } from "../../../api/client";
import { adminApi } from "../../../api/resources";
import type { InvitationAdmin } from "../../../api/types";
import { Modal } from "../../../components/Overlay";
import { Button, DemoBadge, ErrorText, Field, TextInput } from "../../../components/ui";
import { fmtDate } from "../../../utils/labels";
import { LoadState, useLoad } from "../shared";

export function ReviewTab({ reloadKey, onChanged }: { reloadKey: number; onChanged: () => void }) {
  const loader = useCallback(() => adminApi.invitations("NEEDS_REVIEW"), [reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data, error } = useLoad<InvitationAdmin[]>(loader);
  return (
    <div data-testid="review-tab" className="space-y-4">
      <LoadState error={error} loading={!data} testId="review-load-error" />
      {data?.length === 0 && <p data-testid="review-empty" className="rounded-sm border border-dashed border-white/15 px-6 py-14 text-center text-sm text-zinc-500">No registrations need review.</p>}
      {data?.map((inv) => <ReviewCard key={inv.id} inv={inv} onChanged={onChanged} />)}
    </div>
  );
}

function Choice({ label, value, selected, onSelect, testId }: { label: string; value: string; selected: boolean; onSelect: () => void; testId: string }) {
  return (
    <button type="button" onClick={onSelect} data-testid={testId}
      className={`flex-1 rounded-sm border p-4 text-left transition-colors ${selected ? "border-signal bg-signal/10" : "border-white/10 hover:border-white/30"}`}>
      <p className="label-caps text-zinc-500">{label}</p>
      <p className="mt-2 text-lg text-white">{value}</p>
    </button>
  );
}

function ReviewCard({ inv, onChanged }: { inv: InvitationAdmin; onChanged: () => void }) {
  const [useName, setUseName] = useState<"INVITED" | "SUBMITTED">("INVITED");
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const act = async (fn: () => Promise<unknown>, msg: string) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      toast.success(msg);
      setRejectOpen(false);
      onChanged();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <article data-testid={`review-card-${inv.id}`} className="rounded-sm border border-white/10 bg-surface p-6">
      <div className="flex flex-wrap items-center gap-3 text-sm text-zinc-400">
        <span data-testid={`review-email-${inv.id}`} className="text-white">{inv.email}</span>
        <span>· phone <span data-testid={`review-phone-${inv.id}`} className="font-mono text-zinc-200">{inv.submitted_phone_masked}</span></span>
        <span>· submitted {fmtDate(inv.submitted_at)}</span>
        <DemoBadge show={inv.is_demo} />
      </div>
      <p className="label-caps mt-5 text-zinc-500">Name to keep</p>
      <div className="mt-2 flex flex-col gap-3 sm:flex-row">
        <Choice label="Invited name" value={inv.full_name} selected={useName === "INVITED"} onSelect={() => setUseName("INVITED")} testId={`review-choose-invited-${inv.id}`} />
        <Choice label="Submitted name" value={inv.submitted_name ?? ""} selected={useName === "SUBMITTED"} onSelect={() => setUseName("SUBMITTED")} testId={`review-choose-submitted-${inv.id}`} />
      </div>
      <div className="mt-4"><ErrorText message={error} testId={`review-error-${inv.id}`} /></div>
      <div className="mt-5 flex justify-end gap-3">
        <Button variant="secondary" onClick={() => setRejectOpen(true)} data-testid={`review-reject-${inv.id}`}>Reject</Button>
        <Button loading={busy && !rejectOpen} onClick={() => act(() => adminApi.approve(inv.id, useName), "Registration approved. Employee created (phone unverified).")}
          data-testid={`review-approve-${inv.id}`}>Approve</Button>
      </div>
      <Modal open={rejectOpen} onClose={() => setRejectOpen(false)} title="Reject registration" testId="reject-modal">
        <Field label="Reason (required)" htmlFor={`reject-reason-${inv.id}`}>
          <TextInput id={`reject-reason-${inv.id}`} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reject-reason-input" />
        </Field>
        <p className="mt-3 text-xs text-zinc-500">You can send a fresh invitation to this email afterwards.</p>
        <div className="mt-4"><ErrorText message={rejectOpen ? error : null} testId="reject-error" /></div>
        <div className="mt-6 flex justify-end gap-3">
          <Button variant="secondary" onClick={() => setRejectOpen(false)} data-testid="reject-cancel-button">Cancel</Button>
          <Button loading={busy} disabled={reason.trim().length < 3} onClick={() => act(() => adminApi.reject(inv.id, reason.trim()), "Registration rejected")}
            data-testid="reject-confirm-button">Reject</Button>
        </div>
      </Modal>
    </article>
  );
}
